/**
 * useLoanChatUnread(dealId, { active }) — count of chat messages (from OTHERS)
 * since the user last opened this loan's chat, for the floating-bubble badge.
 *
 * The bubble only mounts the chat panel when open, so to show a count while it's
 * CLOSED we do a separate, slow, visibility-aware poll here (and only while
 * `active`, i.e. the bubble is visible AND closed). `markSeen()` stamps a
 * per-loan "last seen" watermark (partner-scoped localStorage) and zeroes the
 * badge — call it whenever the panel is opened/closed.
 *
 * When Web PubSub is live this still works (it just polls a bit); a future
 * enhancement could bump the count off live `chat_message` events instead.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../../../../auth/AuthProvider";
import { loanChatApi } from "../../../../api/loanChatApi";
import { partnerStorage } from "../../../../utils/storage/partnerStorage";

const POLL_MS = 30000;
const seenKey = (dealId) => `loanChat.lastSeenAt.${dealId}`;
const sentAtOf = (m) =>
  typeof m?.sentAt === "string" ? m.sentAt : m?.sentAt?.$date || "";

export function useLoanChatUnread(dealId, { active }) {
  const { user } = useAuth();
  const myEmail = (user?.email || "").toLowerCase();
  const [unread, setUnread] = useState(0);
  // Tracks the loan we've already done an initial fetch for, so re-activating the
  // poller (e.g. when the panel closes) does NOT fire a redundant immediate GET —
  // closing just marked the chat seen, so the count is already known to be 0.
  const initedDealRef = useRef(null);

  const markSeen = useCallback(() => {
    if (!dealId) return;
    try {
      partnerStorage.setItem(seenKey(dealId), new Date().toISOString());
    } catch {
      // ignore storage failures — the badge just won't persist across reloads
    }
    setUnread(0);
  }, [dealId]);

  useEffect(() => {
    if (!active || !dealId) return undefined;
    let cancelled = false;
    const isHidden = () =>
      typeof document !== "undefined" && document.hidden;

    const check = () => {
      if (isHidden()) return;
      loanChatApi
        .getMessages(dealId, { limit: 50 })
        .then((d) => {
          if (cancelled) return;
          let watermark = "";
          try {
            watermark = partnerStorage.getItem(seenKey(dealId)) || "";
          } catch {
            watermark = "";
          }
          const msgs = d?.messages || [];
          const count = msgs.filter((m) => {
            const sender = (m.senderEmail || "").toLowerCase();
            if (!sender || sender === myEmail) return false; // others only
            const at = sentAtOf(m);
            // No watermark yet (never opened the chat) → everything from others
            // counts as unread.
            return watermark ? at > watermark : true;
          }).length;
          setUnread(count);
        })
        .catch(() => {});
    };

    // Fetch immediately only the first time we see this loan; subsequent
    // re-activations (panel open→close) skip straight to the interval.
    if (initedDealRef.current !== dealId) {
      initedDealRef.current = dealId;
      check();
    }
    const t = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (!isHidden()) check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active, dealId, myEmail]);

  return { unread, markSeen };
}

export default useLoanChatUnread;
